import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const EMPTY = {
  name: "",
  description: "",
  cover_url: "",
  category: "",
  verified_only: false,
  featured: false,
};

export function CommunityDialog({ open, onOpenChange, community, onDone }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const editing = !!community;

  useEffect(() => {
    if (open) {
      setForm(
        community
          ? {
              name: community.name || "",
              description: community.description || "",
              cover_url: community.cover_url || "",
              category: community.category || "",
              verified_only: !!community.verified_only,
              featured: !!community.featured,
            }
          : EMPTY
      );
    }
  }, [open, community]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setBusy(true);
    try {
      if (editing) {
        await api.updateCommunity(community.community_id, form);
        toast.success("Community updated");
      } else {
        await api.createCommunity(form);
        toast.success("Community created");
      }
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="community-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit community" : "New community"}</DialogTitle>
          <DialogDescription>
            {editing ? "Update this community's details." : "Create a new community hub for members."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="c-name">Name</Label>
            <Input id="c-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Travellers & Nomads" data-testid="community-name" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="c-desc">Description</Label>
            <Textarea id="c-desc" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="What is this community about?" data-testid="community-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="c-cat">Category</Label>
              <Input id="c-cat" value={form.category} onChange={(e) => set("category", e.target.value)} placeholder="e.g. Lifestyle" data-testid="community-category" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="c-cover">Cover image URL</Label>
              <Input id="c-cover" value={form.cover_url} onChange={(e) => set("cover_url", e.target.value)} placeholder="https://…" data-testid="community-cover" />
            </div>
          </div>

          {form.cover_url && (
            <div className="h-28 rounded-lg overflow-hidden bg-secondary ring-1 ring-border/60">
              <img src={form.cover_url} alt="cover preview" className="h-full w-full object-cover" onError={(e) => (e.currentTarget.style.display = "none")} />
            </div>
          )}

          <div className="flex items-center justify-between rounded-lg bg-secondary/40 px-3 py-2.5">
            <div>
              <div className="text-sm font-medium">Verified members only</div>
              <div className="text-xs text-muted-foreground">Only verified users can join</div>
            </div>
            <Switch checked={form.verified_only} onCheckedChange={(v) => set("verified_only", v)} data-testid="community-verified-only" />
          </div>
          <div className="flex items-center justify-between rounded-lg bg-secondary/40 px-3 py-2.5">
            <div>
              <div className="text-sm font-medium">Featured</div>
              <div className="text-xs text-muted-foreground">Highlight this community in the app</div>
            </div>
            <Switch checked={form.featured} onCheckedChange={(v) => set("featured", v)} data-testid="community-featured" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="community-save">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Save changes" : "Create community"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
